import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { AlertService } from "../../../services/alert.service";
import { S25Util } from "../../../util/s25-util";
import { s25Resource } from "../../../util/s25.resource";
import {
    TrailApi,
    type RequirementRootKind,
    type RequirementRootSummary,
    type RequirementRootDetail,
} from "./trail.api";
import { editorNodeToSaveTree, wireTreeToEditorNode, type RequirementNode } from "./trail.requirement.tree.component";
import { TrailRequirementTreeEditorComponent } from "./trail.requirement.tree.editor.component";
import { TrailLabels } from "./trail.labels";

@Component({
    selector: "s25-ng-trail-requirement-library",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25ButtonComponent, TrailRequirementTreeEditorComponent],
    host: { class: "trail-requirement-library" },
    template: `
        @if (editingRoot()) {
            <div class="trail-requirement-library__editor">
                <div class="trail-requirement-library__header c-margin-bottom--single">
                    <h2 class="trail-requirement-library__editor-title">
                        {{ editingRoot()!.name }}{{ editingRoot()!.code ? " (" + editingRoot()!.code + ")" : "" }}
                    </h2>
                    <s25-ng-button [type]="'outline'" [onClick]="backToRootList">← Back</s25-ng-button>
                </div>
                <div class="trail-requirement-library__toolbar c-margin-bottom--single">
                    @if (editingRoot()!.isPublished) {
                        <span class="trail-requirement-library__badge trail-requirement-library__badge--published"
                            >Published</span
                        >
                    } @else {
                        <span class="trail-requirement-library__badge trail-requirement-library__badge--draft"
                            >Draft</span
                        >
                    }
                </div>
                @if (editorTree()) {
                    <s25-ng-trail-requirement-tree-editor
                        [cohortId]="selectedCohortId()"
                        [rootGroupId]="editingRoot()!.rootGroupId"
                        [node]="editorTree()"
                        [editable]="true"
                        [domain]="'COCURRICULAR'"
                        (nodeChange)="onTreeChange($event)"
                    />
                }
                <div class="trail-requirement-library__actions c-margin-top--single">
                    <s25-ng-button [type]="'primary'" [onClick]="saveTree" [disabled]="saving()">
                        {{ saving() ? "Saving…" : "Save" }}
                    </s25-ng-button>
                    @if (!editingRoot()!.isPublished) {
                        <s25-ng-button [type]="'outline'" [onClick]="publishRoot">Publish</s25-ng-button>
                    }
                </div>
            </div>
        } @else {
            <div class="trail-requirement-library__list">
                <div class="trail-requirement-library__header c-margin-bottom--single">
                    <h2>{{ labels.REQUIREMENT }} Library</h2>
                    <s25-ng-button data-id="back" [type]="'outline'" [onClick]="handleBack">← Back</s25-ng-button>
                </div>
                <div class="trail-requirement-library__toolbar c-margin-bottom--single">
                    <label
                        >{{ labels.COHORT }}:
                        <select
                            class="c-input"
                            data-id="library-cohort"
                            [ngModel]="selectedCohortId()"
                            (ngModelChange)="selectedCohortId.set($event)"
                        >
                            <option [ngValue]="null">Select {{ labels.COHORT.toLowerCase() }}</option>
                            @for (cohort of cohorts.value() ?? []; track cohort.cohortId) {
                                <option [ngValue]="cohort.cohortId">{{ cohort.cohortCode }}</option>
                            }
                        </select>
                    </label>
                    <label
                        >Kind:
                        <select
                            class="c-input"
                            data-id="library-kind"
                            [ngModel]="kindFilter()"
                            (ngModelChange)="kindFilter.set($event)"
                        >
                            <option [ngValue]="null">All</option>
                            <option value="GENERAL">General</option>
                            <option value="DEFN">Curriculum</option>
                            <option value="TRAIL">{{ labels.TRAIL }}</option>
                            <option value="TRACK">{{ labels.TRACK }}</option>
                        </select>
                    </label>
                    <s25-ng-button data-id="refresh-list" [type]="'outline'" [onClick]="refreshList">
                        Refresh
                    </s25-ng-button>
                    <s25-ng-button
                        [type]="'primary'"
                        [onClick]="openCreateDialog"
                        [disabled]="selectedCohortId() == null"
                    >
                        New Root
                    </s25-ng-button>
                </div>
                @if (selectedCohortId() == null) {
                    <p class="c-text--secondary">
                        Select a {{ labels.COHORT.toLowerCase() }} first to view the requirement library.
                    </p>
                } @else if (rootsResource.isLoading()) {
                    <p>Loading…</p>
                } @else if (!filteredRoots().length) {
                    <p class="c-text--secondary">No requirement roots found for this cohort.</p>
                } @else {
                    <table class="c-table trail-requirement-library__table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Code</th>
                                <th>Kind</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (root of filteredRoots(); track root.requirementGroupId) {
                                <tr>
                                    <td>{{ root.name }}</td>
                                    <td>{{ root.code ?? "—" }}</td>
                                    <td>{{ root.kind }}</td>
                                    <td>
                                        <span
                                            class="trail-requirement-library__badge"
                                            [class.trail-requirement-library__badge--published]="root.isPublished"
                                            [class.trail-requirement-library__badge--draft]="!root.isPublished"
                                        >
                                            {{ root.isPublished ? "Published" : "Draft" }}
                                        </span>
                                    </td>
                                    <td>
                                        <div class="trail-requirement-library__actions">
                                            <s25-ng-button [type]="'outline'" [onClick]="editRootFactory(root)"
                                                >Edit</s25-ng-button
                                            >
                                            <s25-ng-button
                                                [type]="'danger--outline'"
                                                [onClick]="disableRootFactory(root)"
                                                >Disable</s25-ng-button
                                            >
                                        </div>
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }
            </div>

            @if (showCreateDialog()) {
                <div class="trail-requirement-library__overlay" (click)="closeCreateDialog()">
                    <div class="trail-requirement-library__dialog" (click)="$event.stopPropagation()">
                        <h3>New Requirement Root</h3>
                        <div class="form-group c-margin-bottom--half">
                            <label for="trail-requirement-library-create-kind">Kind *</label>
                            <select
                                id="trail-requirement-library-create-kind"
                                class="c-input"
                                data-id="create-kind"
                                [ngModel]="createKind()"
                                (ngModelChange)="createKind.set($event)"
                            >
                                <option value="GENERAL">General</option>
                                <option value="DEFN">Curriculum</option>
                                <option value="TRAIL">{{ labels.TRAIL }}</option>
                                <option value="TRACK">{{ labels.TRACK }}</option>
                            </select>
                        </div>
                        <div class="form-group c-margin-bottom--half">
                            <label for="trail-requirement-library-create-name">Name *</label>
                            <input
                                id="trail-requirement-library-create-name"
                                class="c-input"
                                data-id="create-name"
                                [ngModel]="createName()"
                                (ngModelChange)="createName.set($event)"
                                placeholder="Root name"
                            />
                        </div>
                        <div class="form-group c-margin-bottom--half">
                            <label for="trail-requirement-library-create-code">Code *</label>
                            <input
                                id="trail-requirement-library-create-code"
                                class="c-input"
                                data-id="create-code"
                                [ngModel]="createCode()"
                                (ngModelChange)="createCode.set($event)"
                                placeholder="Unique code"
                                required
                            />
                            @if (createCodeConflict()) {
                                <p class="c-text--error c-margin-top--half">
                                    A requirement root with this code already exists in this cohort.
                                </p>
                            }
                        </div>
                        <div class="trail-requirement-library__dialog-actions c-margin-top--single">
                            <s25-ng-button [type]="'primary'" [onClick]="executeCreate" [disabled]="!canCreateRoot()"
                                >Create</s25-ng-button
                            >
                            <s25-ng-button [type]="'danger--outline'" [onClick]="closeCreateDialog"
                                >Cancel</s25-ng-button
                            >
                        </div>
                    </div>
                </div>
            }
        }
    `,
    styles: `
        .trail-requirement-library__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 1rem;
        }
        .trail-requirement-library__header h2 {
            margin: 0;
        }
        .trail-requirement-library__toolbar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem;
        }
        .trail-requirement-library__table {
            width: 100%;
            border-collapse: collapse;
        }
        .trail-requirement-library__table th,
        .trail-requirement-library__table td {
            border: 1px solid var(--color--border, #ddd);
            padding: 0.5rem 0.75rem;
            text-align: left;
        }
        .trail-requirement-library__badge {
            display: inline-block;
            padding: 0.15rem 0.5rem;
            border-radius: 4px;
            font-size: 0.85em;
            font-weight: 600;
        }
        .trail-requirement-library__badge--published {
            background: #d4edda;
            color: #155724;
        }
        .trail-requirement-library__badge--draft {
            background: #fef3cd;
            color: #856404;
        }
        .trail-requirement-library__actions {
            display: flex;
            gap: 0.5rem;
        }
        .trail-requirement-library__editor-title {
            margin: 0;
        }
        .trail-requirement-library__overlay {
            position: fixed;
            inset: 0;
            z-index: 10000;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(0, 0, 0, 0.4);
        }
        .trail-requirement-library__dialog {
            background: var(--color--surface-bg, #fff);
            border-radius: var(--border-radius, 8px);
            padding: 1.5rem;
            max-width: 450px;
            width: 90%;
            box-shadow: var(--shadow--dropdown, 0 8px 32px rgba(0, 0, 0, 0.2));
        }
        .trail-requirement-library__dialog h3 {
            margin: 0 0 1rem;
        }
        .trail-requirement-library__dialog .form-group {
            display: grid;
            grid-template-columns: 5.5rem minmax(0, 1fr);
            column-gap: 0.75rem;
            row-gap: 0.35rem;
            align-items: center;
        }
        .trail-requirement-library__dialog .form-group > label {
            grid-column: 1;
            grid-row: 1;
            margin: 0;
        }
        .trail-requirement-library__dialog .form-group > :not(label) {
            grid-column: 2;
            min-width: 0;
            width: 100%;
            box-sizing: border-box;
        }
        .trail-requirement-library__dialog-actions {
            display: flex;
            gap: 0.75rem;
        }
    `,
})
export class TrailRequirementLibraryComponent {
    readonly labels = TrailLabels;
    readonly cohortId = input<number | null>(null);
    readonly backToAdmin = output<void>();

    readonly listGen = signal(0);
    readonly selectedCohortId = linkedSignal(() => this.cohortId());

    refreshList = () => {
        this.listGen.update((count) => count + 1);
    };

    handleBack = () => {
        this.backToAdmin.emit();
    };

    readonly kindFilter = signal<RequirementRootKind | null>(null);
    readonly editingRoot = signal<RequirementRootDetail | null>(null);
    readonly editorTree = signal<RequirementNode | null>(null);
    readonly saving = signal(false);

    readonly showCreateDialog = signal(false);
    readonly createKind = signal<RequirementRootKind>("GENERAL");
    readonly createName = signal("");
    readonly createCode = signal("");

    readonly cohorts = s25Resource({
        params: () => this.listGen(),
        loader: () => TrailApi.getCohorts(),
    });

    readonly rootsResource = s25Resource({
        if: () => this.selectedCohortId() != null,
        params: () => ({ cohortId: this.selectedCohortId(), gen: this.listGen() }),
        loader: ({ params }) => TrailApi.listRequirementRoots(params.cohortId!),
    });

    readonly filteredRoots = computed((): RequirementRootSummary[] => {
        const roots = this.rootsResource.value() ?? [];
        const kind = this.kindFilter();
        if (!kind) return roots;
        return roots.filter((root) => root.kind === kind);
    });

    readonly existingCodesForCreateKind = computed((): string[] => {
        const kind = this.createKind();
        return (this.rootsResource.value() ?? [])
            .filter((root) => root.kind === kind && root.code)
            .map((root) => root.code as string);
    });

    readonly createCodeConflict = computed(() => {
        return TrailApi.isTrailCodeTaken(this.createCode(), this.existingCodesForCreateKind());
    });

    readonly canCreateRoot = computed(() => {
        return !!this.createName().trim() && !!this.createCode().trim() && !this.createCodeConflict();
    });

    openCreateDialog = () => {
        this.createKind.set("GENERAL");
        this.createName.set("");
        this.createCode.set("");
        this.showCreateDialog.set(true);
    };

    closeCreateDialog = () => {
        this.showCreateDialog.set(false);
    };

    executeCreate = async () => {
        const name = this.createName().trim();
        const code = this.createCode().trim();
        if (!name || !code) return;
        if (this.createCodeConflict()) {
            await AlertService.alert("A requirement root with this code already exists in this cohort.");
            return;
        }
        try {
            const cohortId = this.selectedCohortId();
            if (cohortId == null) return;
            const detail = await TrailApi.createRequirementRoot(cohortId, {
                kind: this.createKind(),
                name,
                code,
            });
            this.showCreateDialog.set(false);
            this.listGen.update((count) => count + 1);
            this.openEditor(detail);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    editRootFactory = (root: RequirementRootSummary) => {
        return async () => {
            try {
                const detail = await TrailApi.getRequirementRoot(root.requirementGroupId);
                this.openEditor(detail);
            } catch (caughtError) {
                await AlertService.alert(S25Util.errorText(caughtError));
            }
        };
    };

    disableRootFactory = (root: RequirementRootSummary) => {
        return async () => {
            if (!(await AlertService.confirm(`Remove "${root.name}" from the library? This is a permanent action.`)))
                return;
            try {
                await TrailApi.disableRequirementRoot(root.requirementGroupId);
                this.listGen.update((count) => count + 1);
            } catch (caughtError) {
                await AlertService.alert(S25Util.errorText(caughtError));
            }
        };
    };

    backToRootList = () => {
        this.editingRoot.set(null);
        this.editorTree.set(null);
        this.listGen.update((count) => count + 1);
    };

    onTreeChange(updated: RequirementNode) {
        this.editorTree.set(updated);
    }

    saveTree = async () => {
        const root = this.editingRoot();
        const tree = this.editorTree();
        if (!root || !tree) return;

        if (root.isPublished) {
            if (
                !(await AlertService.confirm(
                    "This tree is published. Saving will affect active students. This is not a retroactive change. Continue?",
                ))
            ) {
                return;
            }
        }

        this.saving.set(true);
        try {
            await TrailApi.saveRequirementRoot(
                root.rootGroupId,
                editorNodeToSaveTree(tree, this.selectedCohortId() ?? root.cohortId),
            );
            this.backToRootList();
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        } finally {
            this.saving.set(false);
        }
    };

    publishRoot = async () => {
        const root = this.editingRoot();
        if (!root) return;
        if (!(await AlertService.confirm(`Publish "${root.name}"? Once live, this will affect active students.`)))
            return;
        try {
            await TrailApi.publishRequirementRoot(root.rootGroupId);
            const refreshed = await TrailApi.getRequirementRoot(root.rootGroupId);
            this.openEditor(refreshed);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    private openEditor(detail: RequirementRootDetail) {
        this.editingRoot.set(detail);
        this.editorTree.set(wireTreeToEditorNode(detail.requirementTree, this.selectedCohortId() ?? detail.cohortId));
    }
}
